import { create } from 'zustand';

import { getView } from '../engine/prism.js';
import {
  convertSelection,
  EMPTY_SELECTION,
  parseUvLayout,
  type UvLayout,
  type UvSelectMode,
  type UvSelection,
  type Vec2,
} from '../lib/uv-doc.js';
import { logEditor } from './console.js';

/**
 * UV Editor state: select mode and selection (per part), the 2D view,
 * overlays, the engine's read-only layout of non-mesh parts and the status
 * line. The UVs themselves live in the model document (`useModel`).
 */

export type StretchOverlay = 'off' | 'angle' | 'area';
export type UvBackground = 'image' | 'checker' | 'none';

export interface UvOverlays {
  background: UvBackground;
  stretch: StretchOverlay;
  overlap: boolean;
  seams: boolean;
  grid: boolean;
  /** Fill faces (selected faces are always tinted). */
  faces: boolean;
}

export interface UvView {
  /** UV point at the canvas center. */
  center: Vec2;
  /** Canvas pixels per UV unit. */
  zoom: number;
}

/** Shown while G / R / S runs: what it does and the typed value. */
export interface UvModalInfo {
  kind: 'grab' | 'rotate' | 'scale';
  axis: 'u' | 'v' | null;
  text: string;
}

export interface UvState {
  mode: UvSelectMode;
  selection: UvSelection;
  /** Part (prism path) the selection belongs to. */
  selectionPath: string | null;
  view: UvView;
  /** Canvas size in CSS pixels (for framing). */
  size: Vec2;
  overlays: UvOverlays;
  sidebar: boolean;
  /** Texture size for texel density; null = the image's width (else 1024). */
  textureSize: number | null;
  /** Target density for "Set Texel Density", texture pixels per meter. */
  targetDensity: number;
  /** Layout the engine sent for a non-mesh part (`uvLayout` event). */
  engineLayout: UvLayout | null;
  status: string | null;
  modal: UvModalInfo | null;
  /** Last parameters per dialog (Smart UV Project, Pack Islands, ...). */
  params: Record<string, Record<string, number | boolean | string>>;

  setMode: (mode: UvSelectMode, layout: UvLayout | null) => void;
  setSelection: (path: string | null, selection: UvSelection) => void;
  setView: (view: UvView) => void;
  setSize: (size: Vec2) => void;
  setOverlays: (patch: Partial<UvOverlays>) => void;
  toggleSidebar: () => void;
  setTextureSize: (size: number | null) => void;
  setTargetDensity: (density: number) => void;
  setParams: (dialog: string, params: Record<string, number | boolean | string>) => void;
  setStatus: (status: string | null) => void;
  setModal: (modal: UvModalInfo | null) => void;
  layoutFromEngine: (raw: Record<string, unknown>) => void;
  /** Ask the engine for a part's generated UVs (answered by a `uvLayout` event). */
  requestLayout: (path: string) => void;
  /** Run a UV `modelOp` on a part; false (with the reason in the status line) when it failed. */
  runOp: (name: string, path: string, params?: Record<string, unknown>) => boolean;
}

const OVERLAYS_KEY = 'prism.uv.overlays';

export const DEFAULT_UV_OVERLAYS: UvOverlays = { background: 'image', stretch: 'off', overlap: false, seams: true, grid: true, faces: true };

function readOverlays(): UvOverlays {
  try {
    const raw = JSON.parse(localStorage.getItem(OVERLAYS_KEY) ?? '{}') as Partial<UvOverlays>;
    return {
      background: raw.background === 'checker' || raw.background === 'none' ? raw.background : 'image',
      stretch: raw.stretch === 'angle' || raw.stretch === 'area' ? raw.stretch : 'off',
      overlap: raw.overlap === true,
      seams: raw.seams !== false,
      grid: raw.grid !== false,
      faces: raw.faces !== false,
    };
  } catch {
    return DEFAULT_UV_OVERLAYS;
  }
}

export const useUv = create<UvState>((set, get) => ({
  mode: 'vertex',
  selection: EMPTY_SELECTION,
  selectionPath: null,
  view: { center: [0.5, 0.5], zoom: 300 },
  size: [600, 600],
  overlays: typeof localStorage === 'undefined' ? DEFAULT_UV_OVERLAYS : readOverlays(),
  sidebar: true,
  textureSize: null,
  targetDensity: 512,
  engineLayout: null,
  status: null,
  modal: null,
  params: {},

  setMode: (mode, layout) => {
    const { selection } = get();
    set({ mode, selection: layout ? convertSelection(layout, selection, mode) : selection });
  },
  setSelection: (selectionPath, selection) => set({ selectionPath, selection }),
  setView: (view) => {
    const zoom = Math.min(200000, Math.max(8, view.zoom));
    set({ view: { center: view.center, zoom } });
  },
  setSize: (size) => {
    const [w, h] = get().size;
    if (w !== size[0] || h !== size[1]) set({ size });
  },
  setOverlays: (patch) => {
    const overlays = { ...get().overlays, ...patch };
    set({ overlays });
    try {
      localStorage.setItem(OVERLAYS_KEY, JSON.stringify(overlays));
    } catch {
      // Storage unavailable: overlays last for this session.
    }
  },
  toggleSidebar: () => set({ sidebar: !get().sidebar }),
  setTextureSize: (textureSize) => set({ textureSize }),
  setTargetDensity: (targetDensity) => set({ targetDensity }),
  setParams: (dialog, params) => set({ params: { ...get().params, [dialog]: params } }),
  setStatus: (status) => set({ status }),
  setModal: (modal) => set({ modal }),

  layoutFromEngine: (raw) => {
    const layout = parseUvLayout(raw);
    if (layout) set({ engineLayout: layout });
  },

  requestLayout: (path) => {
    const view = getView('model');
    if (!view) return;
    void view.modelCommand({ op: 'modelOp', name: 'uvLayout', path }).then((outcome) => {
      if (!outcome.ok && outcome.error) set({ status: outcome.error });
    });
  },

  runOp: (name, path, params = {}) => {
    const view = getView('model');
    if (!view) {
      set({ status: 'UV operators run in the engine: open the Model view first.' });
      return false;
    }
    // The result comes back as `modelChanged` (one undo step in useModel).
    set({ status: null });
    void view.modelCommand({ op: 'modelOp', name, path, ...params }).then((outcome) => {
      if (outcome.ok) return;
      const message = outcome.error ?? `${name} failed`;
      set({ status: message });
      logEditor('warn', `UV ${name}: ${message}`);
    });
    return true;
  },
}));
